import { ComponentIgnoreOnPdf } from "@components/ComponentIgnoreOnPdf";
import { defaultInvoiceCustomStyle } from "@components/invoice-customization/constants/defaultInvoiceCustomStyle";
import type { InvoiceStyleTokens } from "@components/invoice-customization/utils/createInvoiceStyleTokens";
import { createInvoiceStyleTokens } from "@components/invoice-customization/utils/createInvoiceStyleTokens";
import { AssignmentRounded, CleaningServicesRounded, DescriptionRounded, OpenInNew, PaymentsRounded } from "@mui/icons-material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckIcon from "@mui/icons-material/Check";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { Box, Card, Chip, Link, Typography } from "@mui/material";
import { useMemo } from "react";
import { assets } from "../../../../../assets";
import { ScopeOfWork } from "../../../../scope-of-work/types/scope-of-work.interface";
import { alphaVar, colors } from "../../../../ui/colors";
import { PaymentType } from "../../../create/PaymentMethods";
import { InvoiceCalculations } from "../../../create/shared/types";
import { formatInvoiceAppliesToNote } from "../../../create/shared/utils";
import { formatBillingVisitWindow, humanizeJobType } from "../../utils/formatBillingVisitWindow";
import { isBalanceFullyCoveredByDeposit } from "../../utils/invoiceNetAfterDeposit";
import { getExtraServicesForVisit, getVisitTargetsForExtraItem } from "../../utils/invoiceExtraLineItems";
import { getQuoteGrandTotalFromQuote } from "../../utils/quoteGrandTotal";
import { PaymentSection } from "./payments/PaymentSection";
import { InvoiceSectionIcon } from "@components/invoice-customization/components/InvoiceSectionIcon";
import type { InvoiceCustomStyle, InvoiceIconName } from "@components/invoice-customization/types/invoiceCustomStyle.types";
import { createBodyTextSx } from "@components/invoice-customization/utils/createInvoiceTextStyles";
import type { SvgIconComponent } from "@mui/icons-material";
interface ClientInvoiceHeaderProps {
  isPdfMode?: boolean;
  billingCompanyName?: string;
  billingContactName?: string;
  billingEmail?: string;
  billingAddress?: string;
  paymentStatus: boolean;
  calculations: InvoiceCalculations;
  serviceFeeLabel: string;
  paymentMethods: any;
  paymentType: PaymentType;
  referenceNumber: string;
  canViewPayment: boolean;
  total: number;
  handleChangeStatus?: any;
  onPayPalCaptureSuccess?: () => void;
  selectedPayments: string[];
  invoiceUid?: string;
  // New props for the client invoice details
  scopeOfWork?: ScopeOfWork;
  invoiceDetails: Record<string, any>;
  identifierId?: string; // Invoice identifier for traceability
  issuedDate?: string; // Invoice issued date
  notes?: string;
  invoiceCustomStyle?: InvoiceCustomStyle;
  companyGmail: string;
  companyName: string;
  companyAddress: any;
  companyTimeZone?: string;
}

export const ClientInvoiceHeader = ({
  isPdfMode,
  billingCompanyName,
  billingContactName,
  billingEmail,
  billingAddress,
  calculations,
  serviceFeeLabel,
  paymentMethods,
  paymentStatus,
  handleChangeStatus,
  onPayPalCaptureSuccess,
  paymentType,
  referenceNumber,
  canViewPayment,
  total,
  selectedPayments,
  invoiceUid,
  scopeOfWork,
  invoiceDetails,
  identifierId,
  issuedDate,
  companyGmail,
  notes,
  invoiceCustomStyle = defaultInvoiceCustomStyle,
  companyAddress,
  companyName,
  companyTimeZone,
}: ClientInvoiceHeaderProps) => {
  const invoiceStyleTokens = useMemo(() => createInvoiceStyleTokens(invoiceCustomStyle), [invoiceCustomStyle]);
  const hasPayableClientMethods = (selectedPayments || []).some(
    (provider) => provider === "paypal" || provider === "stripe" || provider === "bank_transfer",
  );

  const billingEvents =
    Array.isArray(invoiceDetails.billingJobEvents) && invoiceDetails.billingJobEvents.length > 0
      ? invoiceDetails.billingJobEvents
      : invoiceDetails.jobEvent
        ? [invoiceDetails.jobEvent]
        : [];

  const primaryJobEvent = billingEvents[0];

  // For deposit invoices, get company from quote
  const isDepositInvoice = billingEvents.length === 0 && invoiceDetails.quote;

  const companyFromInvoice =
    primaryJobEvent?.job?.company ?? invoiceDetails?.jobEvent?.job?.company ?? invoiceDetails?.quote?.company ?? invoiceDetails?.company ?? undefined;

  const effectiveTimeZone =
    companyTimeZone?.trim() ||
    companyFromInvoice?.timeZone?.trim() ||
    invoiceDetails?.jobEvent?.job?.company?.timeZone?.trim() ||
    invoiceDetails?.billingJobEvents?.[0]?.job?.company?.timeZone?.trim() ||
    undefined;

  const defaultTimeZone = effectiveTimeZone;

  const issuedAt = useMemo(() => {
    const rawDate = invoiceDetails?.createdAt || issuedDate;
    if (!rawDate) return "—";
    const dateObj = new Date(rawDate);
    if (isNaN(dateObj.getTime())) return "—";

    if (effectiveTimeZone) {
      try {
        return new Intl.DateTimeFormat("en-US", {
          year: "numeric",
          month: "short",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          timeZone: effectiveTimeZone,
        }).format(dateObj);
      } catch {}
    }

    try {
      return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(dateObj);
    } catch {
      return "—";
    }
  }, [invoiceDetails?.createdAt, issuedDate, effectiveTimeZone]);

  const formattedCompanyAddress = companyFromInvoice
    ? [companyFromInvoice.address, companyFromInvoice.state, companyFromInvoice.zipCode, companyFromInvoice.country].filter(Boolean).join(", ")
    : "";

  const companyAddressLines = [
    companyFromInvoice?.address,
    [companyFromInvoice?.state, companyFromInvoice?.zipCode].filter(Boolean).join(", "),
    companyFromInvoice?.country,
  ].filter(Boolean);

  // Get quote services for deposit invoices
  const quoteServices = isDepositInvoice ? invoiceDetails.quote?.services || [] : [];

  // Currency formatter for deposit amounts (KISS: lean on invoice totalCost, do not try to reprice per-line).
  const depositCurrency = (invoiceDetails?.totalCost?.total?.currency as string) || "USD";
  const depositTotal = Number(invoiceDetails?.totalCost?.total?.value ?? invoiceDetails?.quote?.depositAmount ?? 0);

  const formatMoney = (value: number) => {
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: depositCurrency,
      }).format(value);
    } catch {
      return `$${value.toFixed(2)}`;
    }
  };

  const quoteGrandTotal = isDepositInvoice ? getQuoteGrandTotalFromQuote(invoiceDetails.quote as Record<string, unknown>) : 0;

  // Display labels for the top details card. For deposit invoices we read straight from the quote services
  // because there is no standalone scopeOfWork loaded.
  const primaryService = isDepositInvoice ? quoteServices[0] : undefined;
  const depositAliasLabel = primaryService?.alias || primaryService?.scopeOfWork?.name || invoiceDetails.quote?.alias;
  const depositTypeOfServiceLabel =
    primaryService?.service || primaryService?.scopeOfWork?.typeOfService?.name || primaryService?.typeOfService?.name;

  // Email recipients: surface every CAN_PAY / CAN_VIEW recipient so clients see exactly who received this invoice.
  const emailRecipients: Array<{ email: string; permissionLevel?: string }> = Array.isArray(invoiceDetails?.emailRecipients)
    ? invoiceDetails.emailRecipients
    : [];

  // Quote alias: human-friendly reference set by the admin on the originating quote (e.g. "Smith – Move Out").
  const quoteAlias: string | null = invoiceDetails?.quote?.alias ?? null;

  const uniqueJobsFromBilling = useMemo(() => {
    const m = new Map<
      number,
      {
        id: number;
        alias?: string | null;
        type?: string | null;
        scopeOfWorkUrl?: string | null;
      }
    >();
    for (const ev of billingEvents) {
      const j = ev.job as
        | {
            id?: number;
            alias?: string | null;
            type?: string | null;
            scopeOfWorkUrl?: string | null;
          }
        | undefined;
      if (j?.id != null && !m.has(j.id)) {
        m.set(j.id, {
          id: j.id,
          alias: j.alias,
          type: j.type,
          scopeOfWorkUrl: j.scopeOfWorkUrl ?? null,
        });
      }
    }
    return Array.from(m.values()).sort((a, b) => a.id - b.id);
  }, [billingEvents]);

  const showPerJobServiceLabels = !isDepositInvoice && uniqueJobsFromBilling.length > 1;
  const multiVisitOrMultiJob = !isDepositInvoice && (billingEvents.length > 1 || uniqueJobsFromBilling.length > 1);

  type ClientBillingJobEvent = {
    uid: string;
    jobId: number;
    status: string;
    startDate: string;
    endDate: string;
    description?: string | null;
    job?: {
      id?: number;
      alias?: string | null;
      type?: string | null;
      description?: string | null;
      coverageArea?: { address?: string | null } | null;
      company?: { timeZone?: string | null } | null;
      scopeOfWorkUrl?: string | null;
    };
  };

  /** One UI card per job; visits nested and sorted by start time. */
  const billingEventsByJob = useMemo(() => {
    const m = new Map<number, ClientBillingJobEvent[]>();
    for (const ev of billingEvents as ClientBillingJobEvent[]) {
      const id = ev.job?.id ?? ev.jobId;
      const next = m.get(id);
      if (next) next.push(ev);
      else m.set(id, [ev]);
    }
    for (const list of Array.from(m.values())) {
      list.sort((a, b) => +new Date(a.startDate) - +new Date(b.startDate));
    }
    return Array.from(m.entries()).sort((a, b) => a[0] - b[0]);
  }, [billingEvents]);

  const scopeOfWorkDetailUrls = useMemo(() => {
    const fromJobs = billingEvents
      .map((e: { job?: { scopeOfWorkUrl?: string | null } }) => e.job?.scopeOfWorkUrl)
      .filter((u): u is string => typeof u === "string" && u.length > 0);

    const meta = scopeOfWork?.meta as Record<string, unknown> | null | undefined;

    const metaUrl = typeof meta?.detailUrl === "string" && meta.detailUrl ? meta.detailUrl : "";
    return Array.from(new Set([...fromJobs, metaUrl].filter(Boolean)));
  }, [billingEvents, scopeOfWork?.meta]);

  const serviceSubtotal = Number(
    calculations?.subtotal ?? invoiceDetails?.mainAreasCost?.total?.value ?? invoiceDetails?.totalCost?.total?.value ?? 0,
  );

  const serviceSubtotalSafe = Number.isFinite(serviceSubtotal) ? Math.max(0, serviceSubtotal) : 0;

  const serviceLineAmountByJob = useMemo(() => {
    if (isDepositInvoice || billingEventsByJob.length === 0 || serviceSubtotalSafe <= 0) return new Map<number, number>();

    const savedLines = invoiceDetails?.pricingDetail?.serviceLines ?? calculations?.pricingDetail?.serviceLines;
    if (Array.isArray(savedLines)) {
      const m = new Map<number, number>();
      for (const line of savedLines) {
        if (line && typeof line.jobId === "number" && typeof line.lineTotal === "number") {
          m.set(line.jobId, (m.get(line.jobId) ?? 0) + line.lineTotal);
        }
      }
      if (billingEventsByJob.every(([jobId]) => m.has(jobId))) {
        return m;
      }
    }

    const perJob = serviceSubtotalSafe / billingEventsByJob.length;
    return new Map<number, number>(billingEventsByJob.map(([jobId]) => [jobId, perJob]));
  }, [isDepositInvoice, billingEventsByJob, serviceSubtotalSafe, invoiceDetails?.pricingDetail, calculations?.pricingDetail]);

  const extraLineItems = calculations?.extraLineItems ?? invoiceDetails?.pricingDetail?.extraLineItems ?? [];

  const serviceFees = invoiceDetails?.pricingDetail?.serviceFees ?? calculations?.pricingDetail?.serviceFees ?? [];

  const discounts = calculations?.discounts ?? invoiceDetails?.pricingDetail?.discounts ?? [];

  const invoiceTotal = Number(total ?? 0);
  const tipAmount = Number(calculations?.tipAmount ?? invoiceDetails?.tipAmount ?? invoiceDetails?.pricingDetail?.tip?.amount ?? 0) || 0;

  const depositCreditApplied = Number(invoiceDetails?.creditedDepositAmount ?? 0) || 0;

  const depositInvoiceAmount = invoiceTotal;

  const remainingQuoteBalance =
    isDepositInvoice && quoteGrandTotal > 0 ? Math.max(0, quoteGrandTotal - depositInvoiceAmount) : Math.max(0, invoiceTotal - depositCreditApplied);

  const amountDueNow = paymentStatus
    ? 0
    : isDepositInvoice
      ? Math.max(0, depositInvoiceAmount + tipAmount)
      : Math.max(0, invoiceTotal + tipAmount - depositCreditApplied);

  const paymentSummaryRows = isDepositInvoice
    ? [
        {
          label: "Total service amount",
          helper: "Full approved quote amount.",
          value: formatMoney(quoteGrandTotal),
          tone: "neutral",
        },
        {
          label: paymentStatus ? "Deposit paid on this invoice" : "Deposit due now",
          helper: paymentStatus ? "This deposit has already been paid." : "This is the amount due now.",
          value: formatMoney(depositInvoiceAmount),
          tone: paymentStatus ? "success" : "due",
        },
        quoteGrandTotal > depositInvoiceAmount
          ? {
              label: "Remaining balance after deposit",
              helper: "Not due on this invoice. This balance may be billed later.",
              value: formatMoney(remainingQuoteBalance),
              tone: "muted",
            }
          : null,
        {
          label: paymentStatus ? "Amount paid on this invoice" : "Total due on this invoice",
          helper: paymentStatus ? "No payment is due now." : "Pay this amount to complete this invoice.",
          value: paymentStatus ? formatMoney(depositInvoiceAmount) : formatMoney(amountDueNow),
          tone: paymentStatus ? "successStrong" : "dueStrong",
        },
      ].filter(Boolean)
    : [
        {
          label: "Invoice total",
          value: formatMoney(invoiceTotal),
          tone: "neutral",
        },
        depositCreditApplied > 0
          ? {
              label: "Deposit / credits applied",
              helper: "Already applied to this invoice.",
              value: `-${formatMoney(depositCreditApplied)}`,
              tone: "success",
            }
          : null,
        tipAmount > 0
          ? {
              label: "Tip",
              helper: "Optional amount added by the client.",
              value: formatMoney(tipAmount),
              tone: "neutral",
            }
          : null,
        {
          label: paymentStatus ? "Amount paid" : "Amount due now",
          helper: paymentStatus ? "This invoice has been paid." : "This is the amount due now.",
          value: paymentStatus ? formatMoney(invoiceTotal + tipAmount) : formatMoney(amountDueNow),
          tone: paymentStatus ? "successStrong" : "dueStrong",
        },
      ].filter(Boolean);

  const companyLogoUrl = companyFromInvoice?.avatar
    ? `${companyFromInvoice.avatar}?v=${encodeURIComponent(companyFromInvoice.updatedAt || Date.now())}`
    : undefined;

  return (
    <Card
      data-pdf-section
      sx={{
        borderRadius: invoiceStyleTokens.borders.radius.large,
        border: invoiceStyleTokens.borders.value,
        boxShadow: invoiceStyleTokens.shadow,
        overflow: "hidden",
        mb: 2,
        bgcolor: invoiceStyleTokens.colors.cardBackground,
        fontFamily: invoiceStyleTokens.typography.bodyFontFamily,
      }}
    >
      {/*TOP: Invoice information*/}
      <Box
        sx={{
          px: isPdfMode ? 2.5 : { xs: 2, sm: 3, md: 4 },
          pt: isPdfMode ? 2 : { xs: 2, sm: 3, md: 4 },
          bgcolor: invoiceStyleTokens.colors.pageBackground,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isPdfMode
              ? "minmax(0, 1fr) auto"
              : {
                  xs: "1fr",
                  md: "minmax(0, 1fr) auto",
                },
            gap: 2.5,
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                width: isPdfMode ? 64 : 80,
                height: isPdfMode ? 64 : 80,
                flexShrink: 0,
                borderRadius: isPdfMode ? 2.5 : 4,
                bgcolor: "var(--fx-surface-base, #FFFFFF)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {invoiceDetails?.type === "service_usage" ? (
                <Box
                  component="img"
                  src={assets.images.flownecticon}
                  alt="Flownect"
                  sx={{
                    width: 68,
                    height: 68,
                    objectFit: "contain",
                  }}
                />
              ) : (
                <Box
                  component="img"
                  src={companyLogoUrl}
                  alt={`${companyFromInvoice?.businessName || "Service Provider"} logo`}
                  sx={{
                    width: 68,
                    height: 68,
                    display: "block",
                    objectFit: "contain",
                  }}
                />
              )}
            </Box>

            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                gap: 0.25,
              }}
            >
              <Typography
                sx={{
                  color: invoiceStyleTokens.colors.title,
                  lineHeight: 1.2,
                }}
              >
                From
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: invoiceStyleTokens.colors.title,
                  fontFamily: invoiceStyleTokens.typography.headingFontFamily,
                  fontWeight: 800,
                }}
              >
                {invoiceDetails?.type === "service_usage" ? "Flownect" : companyName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: invoiceStyleTokens.colors.body,
                  overflowWrap: "anywhere",
                  lineHeight: 1.3,
                  fontSize: 12,
                }}
              >
                {invoiceDetails?.type === "service_usage" ? "contact@flownect.com" : companyGmail || "—"}
              </Typography>

              {invoiceDetails?.type !== "service_usage" && formattedCompanyAddress ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: invoiceStyleTokens.colors.body,
                    overflowWrap: "anywhere",
                    lineHeight: 1.3,
                    mt: 0.25,
                  }}
                >
                  {formattedCompanyAddress}
                </Typography>
              ) : null}
            </Box>
          </Box>

          <Box
            sx={{
              textAlign: isPdfMode ? "right" : { xs: "left", md: "right" },
              display: "flex",
              flexDirection: "column",
              alignItems: isPdfMode ? "flex-end" : { xs: "flex-start", md: "flex-end" },
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: isPdfMode ? "nowrap" : "wrap",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  color: invoiceStyleTokens.colors.title,
                  fontFamily: invoiceStyleTokens.typography.headingFontFamily,
                  fontWeight: 900,
                  fontSize: { xs: 28, sm: 30 },
                  letterSpacing: "-0.035em",
                  lineHeight: 1,
                }}
              >
                Invoice
              </Typography>

              <Chip
                label={paymentStatus ? "Paid" : "Payment pending"}
                icon={paymentStatus ? <CheckIcon /> : <AccessTimeIcon />}
                sx={{
                  ...pillBaseSx,
                  height: 28,
                  fontSize: "0.74rem",
                  fontWeight: 800,
                  color: paymentStatus ? semantic.success[800] : semantic.warning[800],
                  bgcolor: paymentStatus ? alphaVar(semantic.success[100], 0.82) : alphaVar(semantic.warning[100], 0.82),
                  "& .MuiChip-icon": {
                    color: paymentStatus ? semantic.success[700] : semantic.warning[700],
                  },
                }}
              />
            </Box>

            <Box marginTop={1}>
              <Typography
                variant="body2"
                sx={{
                  color: invoiceStyleTokens.colors.subTitle,
                  fontWeight: 850,
                  overflowWrap: "anywhere",
                }}
              >
                {identifierId ?? "—"}
              </Typography>

              <Typography variant="caption" sx={{ color: invoiceStyleTokens.colors.body }}>
                Issued {issuedAt}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            ...createSoftSurfaceSx(invoiceStyleTokens),
            p: 1.5,
            mt: 1,
          }}
        >
          <Typography
            sx={{
              color: invoiceStyleTokens.colors.title,
              fontFamily: invoiceStyleTokens.typography.headingFontFamily,
              fontWeight: 800,
            }}
          >
            Bill to
          </Typography>

          {/* Company */}
          {billingCompanyName ? (
            <Typography
              variant="body1"
              sx={{
                color: invoiceStyleTokens.colors.subTitle,
                fontFamily: invoiceStyleTokens.typography.headingFontFamily,
                fontWeight: 300,
              }}
            >
              {billingCompanyName}
            </Typography>
          ) : null}

          {/* Contact */}
          {billingContactName ? (
            <Typography
              sx={{
                color: invoiceStyleTokens.colors.subTitle,
                fontFamily: invoiceStyleTokens.typography.headingFontFamily,
                fontWeight: 300,
              }}
            >
              {billingContactName}
            </Typography>
          ) : null}

          {/* Email */}
          {billingEmail ? (
            <Typography
              sx={{
                ...createBodyTextSx(invoiceStyleTokens),
                overflowWrap: "anywhere",
              }}
            >
              {billingEmail}
            </Typography>
          ) : null}

          {/* Nothing available */}
          {!billingCompanyName && !billingContactName && !billingEmail ? (
            <Typography
              sx={{
                ...createBodyTextSx(invoiceStyleTokens),
              }}
            >
              —
            </Typography>
          ) : null}
        </Box>
      </Box>

      {/*MIDDLE: Services / visits / documents / notes*/}
      <Box
        sx={{
          px: { xs: 2, sm: 3, md: 4 },
          bgcolor: invoiceStyleTokens.colors.pageBackground,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            pt: 1,
          }}
        >
          <Box
            sx={{
              display: "grid",
              mb: 1,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
              },
              gap: invoiceStyleTokens.spacing.cardGap,
            }}
          >
            <InfoTile label="Reference name" value={quoteAlias} styleTokens={invoiceStyleTokens} />

            {!isDepositInvoice && showPerJobServiceLabels ? (
              <InfoTile label="Jobs included" value={`${uniqueJobsFromBilling.length} jobs`} styleTokens={invoiceStyleTokens} />
            ) : null}
          </Box>

          {/* Scheduled visits / Deposit details */}
          <Box
            sx={{
              mb: isPdfMode ? 1 : invoiceStyleTokens.spacing.cardGap,
              boxShadow: isPdfMode ? "none" : invoiceStyleTokens.shadow,
              borderRadius: invoiceStyleTokens.borders.radius.large,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: invoiceStyleTokens.spacing.cardGap,
              }}
            >
              {billingEvents.length === 0 && !isDepositInvoice && invoiceDetails?.type !== "service_usage" ? (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alphaVar(colors.gray[100], 0.45),
                    border: "none",
                  }}
                >
                  <Typography variant="body2" sx={{ color: invoiceStyleTokens.colors.body }}>
                    No visit details are linked to this invoice.
                  </Typography>
                </Box>
              ) : null}

              {billingEventsByJob.map(([jobId, events]) => {
                const job = events[0]?.job;

                const scopeAny = scopeOfWork as any;

                const serviceName =
                  scopeAny?.serviceNameOverride?.trim() ||
                  job?.alias?.trim() ||
                  scopeAny?.typeOfService?.name?.trim() ||
                  scopeAny?.name?.trim() ||
                  humanizeJobType(job?.type) ||
                  `Job #${jobId}`;

                const serviceNameOverride = scopeAny?.serviceNameOverride?.trim() || null;
                const jobDescription = job?.description?.trim() || null;
                const serviceDescription = !serviceNameOverride ? scopeAny?.typeOfService?.description?.trim() || null : null;
                const serviceAddress = job?.coverageArea?.address;
                const tz = job?.company?.timeZone ?? defaultTimeZone;
                const scopeUrl = job?.scopeOfWorkUrl;
                const jobServiceAmount = serviceLineAmountByJob.get(jobId) ?? 0;

                return (
                  <Box
                    key={jobId}
                    data-pdf-keep-together
                    sx={{
                      borderRadius: invoiceStyleTokens.borders.radius.medium,
                      border: invoiceStyleTokens.borders.value,
                      bgcolor: invoiceStyleTokens.colors.cardBackground,
                      overflow: "hidden",
                    }}
                  >
                    {/* Service header */}
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        borderBottom: invoiceStyleTokens.borders.divider,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.75,
                        }}
                      >
                        <InvoiceSectionIcon
                          iconName={invoiceStyleTokens.icons.service}
                          fallbackIcon={CleaningServicesRounded}
                          styleTokens={invoiceStyleTokens}
                        />

                        <Typography
                          sx={{
                            color: invoiceStyleTokens.colors.title,
                            fontFamily: invoiceStyleTokens.typography.headingFontFamily,
                            fontWeight: 800,
                          }}
                        >
                          Service
                        </Typography>
                      </Box>

                      <Typography
                        variant="body2"
                        sx={{
                          color: invoiceStyleTokens.colors.subTitle,
                          fontWeight: 850,
                          lineHeight: 1.25,
                          mt: 0.25,
                        }}
                      >
                        {serviceName}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          color: invoiceStyleTokens.colors.body,
                          fontSize: 12,
                          fontWeight: 700,
                          lineHeight: 1.5,
                        }}
                      >
                        Job #{jobId} · {events.length} scheduled {events.length === 1 ? "visit" : "visits"}
                      </Typography>

                      {serviceDescription ? (
                        <Box sx={{ mt: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: invoiceStyleTokens.colors.subTitle,
                              fontWeight: 800,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                              display: "block",
                              lineHeight: 1.5,
                            }}
                          >
                            Service description
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              color: invoiceStyleTokens.colors.body,
                              fontSize: 12,
                              fontWeight: 700,
                              lineHeight: 1.5,
                              whiteSpace: "pre-wrap",
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                            }}
                          >
                            {serviceDescription}
                          </Typography>
                        </Box>
                      ) : null}

                      {jobDescription ? (
                        <Box sx={{ mt: 1 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: invoiceStyleTokens.colors.subTitle,
                              fontWeight: 800,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                              display: "block",
                              lineHeight: 1.5,
                            }}
                          >
                            Job description
                          </Typography>

                          <Typography
                            variant="body2"
                            sx={{
                              color: invoiceStyleTokens.colors.body,
                              fontSize: 12,
                              fontWeight: 700,
                              lineHeight: 1.5,
                              whiteSpace: "pre-wrap",
                              overflowWrap: "anywhere",
                              wordBreak: "break-word",
                            }}
                          >
                            {jobDescription}
                          </Typography>
                        </Box>
                      ) : null}

                      {serviceAddress ? (
                        <Box sx={{ mt: 1 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: invoiceStyleTokens.colors.subTitle,
                                fontWeight: 800,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                lineHeight: 1.5,
                              }}
                            >
                              Service location
                            </Typography>
                          </Box>

                          <Typography
                            variant="body2"
                            sx={{
                              color: invoiceStyleTokens.colors.body,
                              fontSize: 12,
                              fontWeight: 700,
                              lineHeight: 1.5,
                            }}
                          >
                            {serviceAddress}
                          </Typography>
                        </Box>
                      ) : null}
                    </Box>

                    {/* Scheduled visits */}
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1.1,
                        bgcolor: invoiceStyleTokens.colors.cardBackground,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.75,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: invoiceStyleTokens.colors.subTitle,
                            fontWeight: 800,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          Scheduled visits
                        </Typography>
                      </Box>

                      {events.map((ev, index) => {
                        let visitAmount = 0;
                        const savedLines = invoiceDetails?.pricingDetail?.serviceLines ?? calculations?.pricingDetail?.serviceLines;

                        if (Array.isArray(savedLines)) {
                          const matchedLine = savedLines.find((line: any) => line && line.eventUid === ev.uid);

                          if (matchedLine && typeof matchedLine.lineTotal === "number") {
                            visitAmount = matchedLine.lineTotal;
                          } else {
                            const jobLines = savedLines.filter((l: any) => l && l.jobId === jobId);

                            if (jobLines.length > 0) {
                              const jobTotal = jobLines.reduce((sum: number, l: any) => sum + (l.lineTotal ?? 0), 0);
                              visitAmount = jobTotal / events.length;
                            } else {
                              const jobTotal = serviceLineAmountByJob.get(jobId) ?? 0;
                              visitAmount = jobTotal / events.length;
                            }
                          }
                        } else {
                          const jobTotal = serviceLineAmountByJob.get(jobId) ?? 0;
                          visitAmount = jobTotal / events.length;
                        }

                        const visitExtras = getExtraServicesForVisit(extraLineItems, ev.uid, billingEvents.length, (ev as any).eventUid);
                        const visitExtrasTotal = visitExtras.reduce((sum: number, item) => sum + item.amount, 0);
                        const totalVisitAmount = visitAmount + visitExtrasTotal;

                        return (
                          <Box
                            key={ev.uid}
                            sx={{
                              py: 0.9,
                              display: "grid",
                              gridTemplateColumns: "minmax(0, 1fr) auto",
                              gap: 1.5,
                              alignItems: "center",
                              borderTop: index === 0 ? "none" : invoiceStyleTokens.borders.divider,
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ color: invoiceStyleTokens.colors.subTitle, fontWeight: 750, lineHeight: 1.35 }}>
                                {formatBillingVisitWindow(ev.startDate, ev.endDate, tz)}
                              </Typography>

                              <Typography
                                variant="caption"
                                sx={{
                                  color: invoiceStyleTokens.colors.body,
                                  fontWeight: 600,
                                  textTransform: "capitalize",
                                  display: "block",
                                  mt: 0.2,
                                }}
                              >
                                Visit #{index + 1}
                                {ev.status ? ` · ${ev.status.replace(/_/g, " ")}` : ""}
                              </Typography>

                              {visitExtras.length > 0 && (
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mt: 0.5 }}>
                                  {visitExtras.map((extra, eIdx) => (
                                    <Chip
                                      key={`${extra.id ?? extra.title}-${eIdx}`}
                                      label={`+ ${formatMoney(extra.amount)} ${extra.title}`}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: "0.7rem",
                                        fontWeight: 700,
                                        bgcolor: alphaVar(invoiceStyleTokens.colors.total, 0.08),
                                        color: invoiceStyleTokens.colors.subTitle,
                                        border: `1px solid ${alphaVar(invoiceStyleTokens.colors.total, 0.25)}`,
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}

                              {ev.description ? (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: invoiceStyleTokens.colors.body,
                                    fontWeight: 500,
                                    display: "block",
                                    mt: 0.25,
                                    lineHeight: 1.35,
                                  }}
                                >
                                  {ev.description}
                                </Typography>
                              ) : null}
                            </Box>

                            <Box sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: invoiceStyleTokens.colors.total,
                                  fontWeight: 900,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {formatMoney(totalVisitAmount)}
                              </Typography>
                              {visitExtrasTotal > 0 && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: invoiceStyleTokens.colors.body,
                                    display: "block",
                                    fontSize: "0.68rem",
                                    fontWeight: 600,
                                    textAlign: "right",
                                  }}
                                >
                                  Base: {formatMoney(visitAmount)} + Extra: {formatMoney(visitExtrasTotal)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>

                    {/* Service subtotal */}
                    {(() => {
                      const jobEventsCalculations = events.reduce(
                        (acc, ev) => {
                          let visitAmount = 0;
                          const savedLines = invoiceDetails?.pricingDetail?.serviceLines ?? calculations?.pricingDetail?.serviceLines;

                          if (Array.isArray(savedLines)) {
                            const matchedLine = savedLines.find((line: any) => line && line.eventUid === ev.uid);

                            if (matchedLine && typeof matchedLine.lineTotal === "number") {
                              visitAmount = matchedLine.lineTotal;
                            } else {
                              const jobLines = savedLines.filter((l: any) => l && l.jobId === jobId);

                              if (jobLines.length > 0) {
                                const jobTotal = jobLines.reduce((sum: number, l: any) => sum + (l.lineTotal ?? 0), 0);
                                visitAmount = jobTotal / events.length;
                              } else {
                                const jobTotal = serviceLineAmountByJob.get(jobId) ?? 0;
                                visitAmount = jobTotal / events.length;
                              }
                            }
                          } else {
                            const jobTotal = serviceLineAmountByJob.get(jobId) ?? 0;
                            visitAmount = jobTotal / events.length;
                          }

                          const visitExtras = getExtraServicesForVisit(extraLineItems, ev.uid, billingEvents.length, (ev as any).eventUid);
                          const visitExtrasTotal = visitExtras.reduce((sum: number, item) => sum + item.amount, 0);

                          return {
                            baseTotal: acc.baseTotal + visitAmount,
                            extrasTotal: acc.extrasTotal + visitExtrasTotal,
                            visitTotal: acc.visitTotal + visitAmount + visitExtrasTotal,
                          };
                        },
                        { baseTotal: 0, extrasTotal: 0, visitTotal: 0 },
                      );

                      const unallocatedExtraLineItems = extraLineItems.filter((item) => {
                        const targetInfo = getVisitTargetsForExtraItem(item, billingEvents);
                        return targetInfo.targets.length === 0;
                      });

                      if (isDepositInvoice || (jobEventsCalculations.visitTotal <= 0 && jobServiceAmount <= 0 && serviceSubtotalSafe <= 0)) {
                        return null;
                      }

                      return (
                        <>
                          <Box
                            sx={{
                              borderTop: invoiceStyleTokens.borders.divider,
                              px: 1.5,
                              py: 1.2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1.5,
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: invoiceStyleTokens.colors.subTitle,
                                  fontWeight: 850,
                                  lineHeight: 1.25,
                                }}
                              >
                                Service subtotal
                              </Typography>

                              <Typography
                                variant="caption"
                                sx={{
                                  color: invoiceStyleTokens.colors.body,
                                  fontSize: 12,
                                  fontWeight: 700,
                                  lineHeight: 1.5,
                                }}
                              >
                                Total for the scheduled visits listed above.
                              </Typography>
                            </Box>

                            <Box sx={{ textAlign: "right", whiteSpace: "nowrap" }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: invoiceStyleTokens.colors.total,
                                  fontWeight: 950,
                                }}
                              >
                                {formatMoney(jobEventsCalculations.visitTotal || jobServiceAmount || serviceSubtotalSafe)}
                              </Typography>
                              {jobEventsCalculations.extrasTotal > 0 && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: invoiceStyleTokens.colors.body,
                                    display: "block",
                                    fontSize: "0.68rem",
                                    fontWeight: 600,
                                    textAlign: "right",
                                  }}
                                >
                                  Base: {formatMoney(jobEventsCalculations.baseTotal || jobServiceAmount || serviceSubtotalSafe)} + Extra: {formatMoney(jobEventsCalculations.extrasTotal)}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* Additional charges within the service summary (only unallocated extra services & service fees) */}
                          {unallocatedExtraLineItems.length > 0 || serviceFees.length > 0 ? (
                            <Box
                              data-pdf-keep-together
                              sx={{
                                borderRadius: invoiceStyleTokens.borders.radius.medium,
                                border: invoiceStyleTokens.borders.value,
                                bgcolor: invoiceStyleTokens.colors.cardBackground,
                                overflow: "hidden",
                              }}
                            >
                              {unallocatedExtraLineItems.map((item, idx) => {
                                const targetInfo = getVisitTargetsForExtraItem(item, billingEvents);

                                return (
                                  <Box
                                    key={`extra-${item.title}-${idx}`}
                                    sx={{
                                      px: 1.5,
                                      py: 1.1,
                                      display: "grid",
                                      gridTemplateColumns: "minmax(0, 1fr) auto",
                                      gap: 1.5,
                                      alignItems: "start",
                                      borderTop: idx === 0 ? "none" : invoiceStyleTokens.borders.divider,
                                    }}
                                  >
                                    <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.35 }}>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: invoiceStyleTokens.colors.body }}>
                                          {item.title || "Additional charge"}
                                        </Typography>
                                        {targetInfo.summaryLabel ? (
                                          <Chip
                                            label={targetInfo.summaryLabel}
                                            size="small"
                                            sx={{
                                              height: 20,
                                              fontSize: "0.68rem",
                                              fontWeight: 700,
                                              bgcolor: targetInfo.isAllVisits
                                                ? alphaVar(invoiceStyleTokens.colors.total, 0.12)
                                                : alphaVar(invoiceStyleTokens.colors.body, 0.08),
                                              color: targetInfo.isAllVisits
                                                ? invoiceStyleTokens.colors.subTitle
                                                : invoiceStyleTokens.colors.body,
                                              border: `1px solid ${
                                                targetInfo.isAllVisits
                                                  ? alphaVar(invoiceStyleTokens.colors.total, 0.28)
                                                  : alphaVar(invoiceStyleTokens.colors.body, 0.2)
                                              }`,
                                            }}
                                          />
                                        ) : null}
                                      </Box>

                                      {item.description ? (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: invoiceStyleTokens.colors.body,
                                            display: "block",
                                            mt: 0.15,
                                          }}
                                        >
                                          {item.description}
                                        </Typography>
                                      ) : null}
                                    </Box>

                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: invoiceStyleTokens.colors.total,
                                        fontWeight: 950,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {formatMoney(Number(item.amount ?? 0))}
                                    </Typography>
                                  </Box>
                                );
                              })}

                              {serviceFees.map((fee: any, index: number) => {
                                const amount = Number(fee.total?.value ?? fee.value ?? fee.amount ?? 0);

                                return (
                                  <Box
                                    key={`service-fee-${index}`}
                                    sx={{
                                      px: 1.5,
                                      py: 1.1,
                                      display: "grid",
                                      gridTemplateColumns: "minmax(0, 1fr) auto",
                                      gap: 1.5,
                                      alignItems: "start",
                                      borderTop: unallocatedExtraLineItems.length === 0 && index === 0 ? "none" : invoiceStyleTokens.borders.divider,
                                    }}
                                  >
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 700,
                                          color: invoiceStyleTokens.colors.body,
                                        }}
                                      >
                                        {fee.alias || "Service fee"}
                                      </Typography>

                                      {fee.appliesTo?.label ? (
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: invoiceStyleTokens.colors.body,
                                            display: "block",
                                            mt: 0.35,
                                          }}
                                        >
                                          {formatInvoiceAppliesToNote(fee.appliesTo.label)}
                                        </Typography>
                                      ) : null}
                                    </Box>

                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: invoiceStyleTokens.colors.total,
                                        fontWeight: 950,
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {formatMoney(Number.isFinite(amount) ? amount : 0)}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                          ) : null}
                        </>
                      );
                    })()}

                    {/* Scope of work link */}
                    {scopeUrl && !isPdfMode ? (
                      <ComponentIgnoreOnPdf>
                        <Box sx={{ px: 1.5, pb: 1.25 }}>
                          <Link
                            href={scopeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="none"
                            sx={{
                              width: "fit-content",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 0.75,
                              px: 1.25,
                              py: 0.75,
                              borderRadius: "999px",
                              fontWeight: 850,
                              fontSize: "0.78rem",
                              color: invoiceStyleTokens.colors.title,
                              bgcolor: alphaVar(invoiceStyleTokens.colors.title, 0.09),

                              "&:hover": {
                                bgcolor: alphaVar(invoiceStyleTokens.colors.title, 0.14),
                              },
                            }}
                          >
                            Open full service details
                            <OpenInNew sx={{ fontSize: 14 }} />
                          </Link>
                        </Box>
                      </ComponentIgnoreOnPdf>
                    ) : null}
                  </Box>
                );
              })}

              {isDepositInvoice &&
                quoteServices.map((service: any, idx: number) => {
                  const serviceType = service.service || service.scopeOfWork?.typeOfService?.name || service.typeOfService?.name || "Service";
                  const alias = service.alias || service.scopeOfWork?.name;
                  const showAlias = alias && alias !== serviceType;
                  const unitPrice = Number(service.price);
                  const units = Number(service.units);
                  const lineTotal = Number(service.total);
                  const hasLineBreakdown = !Number.isNaN(unitPrice) && !Number.isNaN(lineTotal) && lineTotal > 0;

                  return (
                    <Box
                      key={service.serviceUid || service.id || idx}
                      data-pdf-keep-together
                      sx={{
                        p: 1.35,

                        bgcolor: invoiceStyleTokens.colors.cardBackground,

                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) auto",
                        gap: 1.25,
                        alignItems: "center",
                        borderRadius: invoiceStyleTokens.borders.radius.medium,
                        border: invoiceStyleTokens.borders.value,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: invoiceStyleTokens.colors.body,
                            fontWeight: 900,
                            lineHeight: 1.3,
                          }}
                        >
                          {serviceType}
                        </Typography>

                        {showAlias ? (
                          <Typography
                            variant="caption"
                            sx={{
                              color: invoiceStyleTokens.colors.body,
                              fontWeight: 700,
                            }}
                          >
                            {alias}
                          </Typography>
                        ) : null}

                        {hasLineBreakdown ? (
                          <Typography
                            variant="caption"
                            sx={{
                              color: invoiceStyleTokens.colors.body,
                              display: "block",
                              mt: 0.35,
                            }}
                          >
                            {!Number.isNaN(units) ? `${units} ${service.measure || ""}`.trim() + " × " : ""}
                            {formatMoney(unitPrice)}
                          </Typography>
                        ) : null}
                      </Box>

                      {hasLineBreakdown ? (
                        <Typography
                          variant="body2"
                          sx={{
                            color: invoiceStyleTokens.colors.body,
                            fontWeight: 950,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {formatMoney(lineTotal)}
                        </Typography>
                      ) : null}
                    </Box>
                  );
                })}

              {!isDepositInvoice && billingEventsByJob.length > 1 && serviceSubtotalSafe > 0 ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: invoiceStyleTokens.colors.body,
                    lineHeight: 1.45,
                  }}
                >
                  Service subtotal is distributed across the billed jobs shown above.
                </Typography>
              ) : null}

              {isDepositInvoice ? (
                <Box
                  sx={{
                    borderRadius: invoiceStyleTokens.borders.radius.medium,
                    border: invoiceStyleTokens.borders.value,
                    bgcolor: invoiceStyleTokens.colors.cardBackground,
                    overflow: "hidden",
                  }}
                >
                  {quoteGrandTotal > 0 ? (
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1.05,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: invoiceStyleTokens.colors.subTitle, fontWeight: 800 }}>
                        Quote total
                      </Typography>
                      <Typography variant="body2" sx={{ color: invoiceStyleTokens.colors.total, fontWeight: 950 }}>
                        {formatMoney(quoteGrandTotal)}
                      </Typography>
                    </Box>
                  ) : null}

                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: invoiceStyleTokens.colors.subTitle, fontWeight: 900 }}>
                      Deposit amount
                    </Typography>
                    <Typography variant="body2" sx={{ color: invoiceStyleTokens.colors.total, fontWeight: 950 }}>
                      {formatMoney(depositTotal)}
                    </Typography>
                  </Box>

                  {quoteGrandTotal > depositTotal ? (
                    <Box
                      sx={{
                        px: 1.5,
                        pb: 1.1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: invoiceStyleTokens.colors.subTitle, fontWeight: 700 }}>
                        Remaining balance after deposit
                      </Typography>
                      <Typography variant="caption" sx={{ color: invoiceStyleTokens.colors.body, fontWeight: 900 }}>
                        {formatMoney(quoteGrandTotal - depositTotal)}
                      </Typography>
                    </Box>
                  ) : null}
                </Box>
              ) : null}
            </Box>
          </Box>

          {discounts.length > 0 ? (
            <Box
              data-pdf-keep-together
              sx={{
                mb: invoiceStyleTokens.spacing.cardGap,
                borderRadius: invoiceStyleTokens.borders.radius.medium,
                border: invoiceStyleTokens.borders.value,
                bgcolor: invoiceStyleTokens.colors.cardBackground,
                boxShadow: invoiceStyleTokens.shadow,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 1.5,
                  pt: 1.35,
                  pb: 1.1,
                }}
              >
                <SectionHeader
                  title="Discounts"
                  description="Discounts applied to this invoice."
                  styleTokens={invoiceStyleTokens}
                  iconName={invoiceStyleTokens.icons.additionalCharges}
                  fallbackIcon={AssignmentRounded}
                  disableMargin
                />
              </Box>

              {discounts.map((item, idx) => {
                const appliesToNote = formatInvoiceAppliesToNote(item.appliesTo);
                return (
                  <Box
                    key={`${item.description}-${idx}`}
                    sx={{
                      px: 1.5,
                      py: 1.1,
                      display: "grid",
                      gridTemplateColumns: "minmax(0, 1fr) auto",
                      gap: 1.5,
                      alignItems: "start",
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: invoiceStyleTokens.colors.body }}>
                        {item.description}
                      </Typography>
                      {appliesToNote ? (
                        <Typography
                          variant="caption"
                          sx={{
                            color: invoiceStyleTokens.colors.body,
                            display: "block",
                            mt: 0.35,
                            lineHeight: 1.4,
                            opacity: 0.8,
                          }}
                        >
                          {appliesToNote}
                        </Typography>
                      ) : null}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: invoiceStyleTokens.colors.total,
                        fontWeight: 950,
                        whiteSpace: "nowrap",
                      }}
                    >
                      -{formatMoney(item.amount)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ) : null}

          {/* Notes */}
          {notes ? (
            <Box
              data-pdf-keep-together
              sx={{
                mb: invoiceStyleTokens.spacing.cardGap,
                borderRadius: invoiceStyleTokens.borders.radius.medium,
                border: invoiceStyleTokens.borders.value,
                bgcolor: invoiceStyleTokens.colors.cardBackground,
                boxShadow: invoiceStyleTokens.shadow,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  px: 1.5,
                  pt: 1.35,
                  pb: 1.1,
                }}
              >
                <SectionHeader
                  title="Notes from the provider"
                  description="Additional information about this invoice or service."
                  styleTokens={invoiceStyleTokens}
                  iconName={invoiceStyleTokens.icons.notes}
                  fallbackIcon={DescriptionRounded}
                  disableMargin
                />
              </Box>

              <Box sx={{ px: 1.5, py: 1.25 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: invoiceStyleTokens.colors.body,
                    lineHeight: 1.5,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                  }}
                >
                  {notes}
                </Typography>
              </Box>
            </Box>
          ) : null}
        </Box>
      </Box>

      {/*BOTTOM: Payment*/}
      <Box
        sx={{
          px: isPdfMode ? 2.5 : { xs: 2, sm: 3, md: 4 },
          pb: isPdfMode ? 1.25 : { xs: 2, sm: 3, md: 2 },
          bgcolor: invoiceStyleTokens.colors.pageBackground,
          boxShadow: isPdfMode ? "none" : invoiceStyleTokens.shadow,
        }}
      >
        <Box
          data-pdf-keep-together
          sx={{
            p: isPdfMode ? 1.25 : { xs: 1.75, sm: 1.25 },

            minWidth: 0,

            borderRadius: invoiceStyleTokens.borders.radius.medium,
            border: invoiceStyleTokens.borders.value,
            bgcolor: invoiceStyleTokens.colors.cardBackground,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 0.75,
            }}
          >
            <InvoiceSectionIcon iconName={invoiceStyleTokens.icons.payment} fallbackIcon={PaymentsRounded} styleTokens={invoiceStyleTokens} />

            <Typography
              sx={{
                color: invoiceStyleTokens.colors.title,
                fontFamily: invoiceStyleTokens.typography.headingFontFamily,
                fontWeight: 800,
              }}
            >
              {isDepositInvoice ? "Deposit Payment Details" : isBalanceFullyCoveredByDeposit(invoiceDetails) ? "Payment Status" : "Payment Details"}
            </Typography>
          </Box>

          <Typography
            sx={{
              ...createBodyTextSx(invoiceStyleTokens),
            }}
          >
            {paymentStatus
              ? isDepositInvoice
                ? "This deposit invoice has been paid. The remaining service balance is shown below for reference."
                : "This invoice has been paid. The summary below shows the total amount covered."
              : isDepositInvoice
                ? "This invoice is for the deposit only. The remaining service balance is shown below for clarity and will be due later."
                : !hasPayableClientMethods
                  ? "This invoice has no online payment methods. The balance summary is shown below."
                  : canViewPayment
                    ? "Review the amount due and choose one of the available payment options below."
                    : "The balance summary is shown below. Payment options are only available to authorized payment users."}
          </Typography>

          {isDepositInvoice && !paymentStatus ? (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: alphaVar(invoiceStyleTokens.colors.subTitle, 0.08),
                border: "none",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.25,
              }}
            >
              {/* deposit notice */}
            </Box>
          ) : null}

          <Box
            sx={{
              minWidth: 0,
              maxWidth: "100%",
              overflowX: "auto",
              boxShadow: invoiceStyleTokens.shadow,
            }}
          >
            {/* Invoice total */}
            <Box
              data-pdf-keep-together
              sx={{
                mt: isPdfMode ? 1.25 : 2,
                mb: isPdfMode ? 1.25 : 2,
                p: isPdfMode ? 1.25 : 2,
                borderRadius: invoiceStyleTokens.borders.radius.medium,
                border: invoiceStyleTokens.borders.value,
                bgcolor: invoiceStyleTokens.colors.cardBackground,
                boxShadow: isPdfMode ? "none" : invoiceStyleTokens.shadow,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      color: invoiceStyleTokens.colors.title,
                      fontFamily: invoiceStyleTokens.typography.headingFontFamily,
                      fontWeight: 900,
                      fontSize: 18,
                    }}
                  >
                    Invoice Total
                  </Typography>

                  <Typography variant="caption" sx={{ color: invoiceStyleTokens.colors.body }}>
                    Final amount for this invoice.
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    color: invoiceStyleTokens.colors.total,
                    fontFamily: invoiceStyleTokens.typography.headingFontFamily,
                    fontWeight: 950,
                    fontSize: 24,
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatMoney(invoiceTotal)}
                </Typography>
              </Box>
            </Box>

            <PaymentSection
              paymentStatus={paymentStatus}
              handleChangeStatus={handleChangeStatus}
              onPayPalCaptureSuccess={onPayPalCaptureSuccess}
              paymentMethods={paymentMethods}
              paymentType={paymentType}
              referenceNumber={referenceNumber}
              canViewPayment={canViewPayment}
              total={total}
              selectedPayments={selectedPayments}
              invoiceUid={invoiceUid}
              calculations={calculations}
              serviceFeeLabel={serviceFeeLabel}
              isDeposit={isDepositInvoice}
              balanceCoveredByDeposit={isBalanceFullyCoveredByDeposit(invoiceDetails)}
              depositCreditApplied={Number(invoiceDetails?.creditedDepositAmount ?? 0) || null}
              depositCreditCurrency={depositCurrency}
              invoiceType={invoiceDetails?.type}
              isPdfMode={isPdfMode}
              invoiceDetails={invoiceDetails}
              styleTokens={invoiceStyleTokens}
            />
          </Box>
        </Box>
      </Box>

      <Box
        data-pdf-keep-together
        sx={{
          bgcolor: invoiceStyleTokens.colors.pageBackground,
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3, md: 4 },
            pb: 2,
            bgcolor: invoiceStyleTokens.colors.pageBackground,
          }}
        >
          <Box
            sx={{
              borderRadius: 1.25,
              bgcolor: alphaVar(invoiceStyleTokens.colors.title, 0.04),
              border: `1px solid ${alphaVar(invoiceStyleTokens.colors.title, 0.06)}`,
              display: "flex",
              flexDirection: "column",
              gap: isPdfMode ? 1 : 1.5,
              p: isPdfMode ? 1.25 : 2,
            }}
          >
            {/* Encabezado e Instrucción Principal */}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  color: invoiceStyleTokens.colors.title,
                  fontSize: "0.875rem",
                  lineHeight: 1.2,
                }}
              >
                Paying by check?
              </Typography>

              <Typography
                sx={{
                  color: invoiceStyleTokens.colors.body,
                  fontWeight: 500,
                  fontSize: "0.775rem",
                  lineHeight: 1.3,
                }}
              >
                Please send your check to:
              </Typography>
            </Box>

            {/* Caja Destacada con Dirección y Beneficiario */}
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: alphaVar(invoiceStyleTokens.colors.title, 0.05),
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              {/* Dirección */}
              <Box>
                {companyAddressLines.length > 0 ? (
                  companyAddressLines.map((line, index) => (
                    <Typography
                      key={index}
                      sx={{
                        fontWeight: 700,
                        color: invoiceStyleTokens.colors.title,
                        fontSize: "0.8rem",
                        lineHeight: 1.3,
                      }}
                    >
                      {line}
                    </Typography>
                  ))
                ) : (
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: invoiceStyleTokens.colors.title,
                      fontSize: "0.8rem",
                      lineHeight: 1.3,
                    }}
                  >
                    —
                  </Typography>
                )}
              </Box>

              {/* Nombre a quien se emite el pago */}
              <Typography
                sx={{
                  color: invoiceStyleTokens.colors.body,
                  fontWeight: 500,
                  fontSize: "0.775rem",
                  lineHeight: 1.3,
                }}
              >
                Make payable to:{" "}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    color: invoiceStyleTokens.colors.title,
                  }}
                >
                  {companyName || "—"}
                </Box>
              </Typography>
            </Box>

            {/* Nota final */}
            <Typography
              sx={{
                color: invoiceStyleTokens.colors.body,
                fontWeight: 500,
                fontSize: "0.775rem",
                lineHeight: 1.3,
              }}
            >
              Please notify us once the check has been sent.
            </Typography>
          </Box>
        </Box>

        {/*Footer*/}
        <Box
          sx={{
            bgcolor: invoiceStyleTokens.colors.pageBackground,
            px: { xs: 2, sm: 3, md: 4 },
            py: 2.5,

            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
            <LockOpenIcon
              sx={{
                color: invoiceStyleTokens.colors.title,
                fontSize: 19,
                mt: 0.1,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: invoiceStyleTokens.colors.body,
                fontWeight: 500,
                fontSize: "0.72rem",
                lineHeight: 1.45,
              }}
            >
              <Box component="span" sx={{ fontWeight: 750, color: invoiceStyleTokens.colors.body }}>
                Secure Invoice Access:
              </Box>
              You are viewing this invoice through a secure client link. Only authorized users can make payments.
            </Typography>
          </Box>

          <Typography
            variant="caption"
            sx={{
              color: invoiceStyleTokens.colors.body,
              fontWeight: 750,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              fontSize: "0.58rem",
              whiteSpace: "nowrap",
            }}
          >
            ID: {identifierId ?? "—"}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
};

interface SectionHeaderProps {
  title: string;
  description?: string;
  styleTokens: InvoiceStyleTokens;
  iconName?: InvoiceIconName;
  fallbackIcon?: SvgIconComponent;
  disableMargin?: boolean;
}

const SectionHeader = ({ title, description, styleTokens, iconName, fallbackIcon, disableMargin = false }: SectionHeaderProps) => (
  <Box
    sx={{
      mb: disableMargin ? 0 : 1.35,
      display: "flex",
      alignItems: "flex-start",
      gap: 1,
    }}
  >
    {iconName && fallbackIcon ? <InvoiceSectionIcon iconName={iconName} fallbackIcon={fallbackIcon} styleTokens={styleTokens} /> : null}

    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          color: styleTokens.colors.title,
          fontFamily: styleTokens.typography.headingFontFamily,
          fontWeight: 900,
          fontSize: 18,
          lineHeight: 1.2,
          letterSpacing: "-0.01em",
          mb: description ? 0.35 : 0,
        }}
      >
        {title}
      </Typography>

      {description ? (
        <Typography
          sx={{
            color: styleTokens.colors.body,
            fontFamily: styleTokens.typography.bodyFontFamily,
            lineHeight: 1.5,
            fontSize: 12,
            fontWeight: 700,
            maxWidth: 820,
          }}
        >
          {description}
        </Typography>
      ) : null}
    </Box>
  </Box>
);

const InfoTile = ({ label, value, styleTokens }: { label: string; value?: React.ReactNode; styleTokens: InvoiceStyleTokens }) => {
  if (!value) return null;

  return (
    <Box
      sx={{
        minWidth: 0,
        p: 1.5,
        borderRadius: styleTokens.borders.radius.large,
        bgcolor: styleTokens.colors.cardBackground,
        border: styleTokens.borders.value,
        boxShadow: styleTokens.shadow,
      }}
    >
      <Typography
        sx={{
          color: styleTokens.colors.body,
          fontWeight: 800,
          fontSize: 10,
          textTransform: "uppercase",
          lineHeight: 1,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          color: styleTokens.colors.body,
          fontSize: 14,
          fontWeight: 900,
          lineHeight: 1.25,
          mt: 0.75,
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

const semantic = {
  success: {
    100: "#DCFCE7",
    600: "#16A34A",
    700: "#15803D",
    800: "#166534",
  },
  warning: {
    100: "#FEF3C7",
    600: "#D97706",
    700: "#B45309",
    800: "#92400E",
  },
};

const pillBaseSx = {
  borderRadius: 16,
  boxShadow: "none",
  border: "none",
  height: 28,
  fontSize: 10,
  fontWeight: 800,
  "& .MuiChip-label": {
    px: 1.1,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};

const createSoftSurfaceSx = (styleTokens: InvoiceStyleTokens) => ({
  borderRadius: styleTokens.borders.radius.large,
  bgcolor: styleTokens.colors.cardBackground,
  border: styleTokens.borders.value,
  boxShadow: styleTokens.shadow,
});
